"use client"

import * as React from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { TableHead } from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

interface SortableTableHeadProps {
  headerId: string
  /** Column id mirrored to a data-col-id attribute so the parent can measure
   *  rendered widths (frozen-width cache for empty/filtered states). */
  colId?: string
  /** Resolved width/min/max style from the parent's proportional distribution
   *  (overrides the px width/minWidth/maxWidth below when provided). */
  widthStyle?: React.CSSProperties
  width: number
  minWidth: number
  maxWidth: number
  canResize: boolean
  isResizing: boolean
  resizeHandler: (event: unknown) => void
  resetSize: () => void
  children: React.ReactNode
  /** aria-sort value — derived from column.getIsSorted() */
  ariaSortDir?: false | "asc" | "desc"
}

export function SortableTableHead({
  headerId,
  colId,
  widthStyle,
  width,
  minWidth,
  maxWidth,
  canResize,
  isResizing,
  resizeHandler,
  resetSize,
  children,
  ariaSortDir,
}: SortableTableHeadProps) {
  // dnd-kit announces the drag handle as "sortable"; say it in the interface language.
  const { t } = useShellTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
    index: currentIndex,
  } = useSortable({ id: headerId, attributes: { roleDescription: t("shell.table.drag.roleDescription") } })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    // Proportional width from the parent (%, with min-width) takes precedence;
    // fall back to the legacy px width when not provided.
    width: widthStyle?.width ?? `${width}px`,
    minWidth: widthStyle?.minWidth ?? `${minWidth}px`,
    maxWidth: widthStyle?.maxWidth ?? `${maxWidth}px`,
    opacity: isDragging ? 0.4 : 1,
    // Freeze the header on vertical scroll. Sticky must live on the <th>
    // itself (sticky on <thead>/<tr> is unreliable cross-browser). While a
    // column is being drag-reordered we drop back to `relative` so dnd-kit's
    // transform tracks the pointer without the sticky offset fighting it.
    position: isDragging ? "relative" : "sticky",
    top: 0,
  }

  const showDropIndicator = isOver && active && active.id !== headerId
  const activeIndex = active?.data?.current?.sortable?.index ?? -1
  const dropSide = showDropIndicator
    ? activeIndex < currentIndex ? "right" : "left"
    : null

  const ariaSortValue: React.AriaAttributes["aria-sort"] =
    ariaSortDir === "asc"
      ? "ascending"
      : ariaSortDir === "desc"
        ? "descending"
        : ariaSortDir === false
          ? "none"
          : undefined

  return (
    <TableHead
      ref={setNodeRef}
      data-col-id={colId}
      style={style}
      aria-sort={ariaSortValue}
      className={cn(
        // #337 — the SAME two rules the pinned branch got on 2026-08-17.
        // They were applied only there, and this is the branch every
        // unpinned column takes, so the reported page never changed:
        // "In Progress" + its 16px arrow needs more than the 100px the
        // table-fixed column allots, and the excess drew over the next
        // header. `overflow-hidden` is what ends it (body cells already
        // clip); `whitespace-normal` lets the label take a second line
        // rather than being sliced. `h-11` must stay — the empty-state
        // overlay is pinned at `top-11`.
        "h-11 px-2 first:ps-4 last:pe-4 align-middle text-start overflow-hidden [&_button]:whitespace-normal [&_button]:text-start",
        // Solid background so scrolled-under body rows don't bleed through the
        // now-frozen header; z-index above body cells.
        "bg-background z-20",
        isDragging && "z-30"
      )}
    >
      {dropSide === "left" && (
        <div className="absolute start-0 top-0 bottom-0 w-0.5 bg-blue-500 z-20" />
      )}
      {dropSide === "right" && (
        <div className="absolute end-0 top-0 bottom-0 w-0.5 bg-blue-500 z-20" />
      )}

      {/* Draggable content area — resize handle is excluded.
          FIX: Bug 6 — column sort buttons live inside this wrapper, which
          spreads dnd-kit's {listeners} (onPointerDown). Without the guard
          below, dnd-kit captures the pointerdown on the sort button, starts
          tracking a potential drag, and then suppresses the synthetic React
          onClick when the pointer lifts, making sort appear to be a no-op.
          We intercept onPointerDown from the spread and bail out early when
          the target is a <button> so the click propagates normally. */}
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        suppressHydrationWarning
        onPointerDown={(e) => {
          // Allow <button> elements (sort triggers) to handle their own click.
          if ((e.target as HTMLElement).closest("button")) return
          listeners?.onPointerDown?.(e)
        }}
        className={cn(
          // Needed here and NOT in the pinned branch: this button is a flex
          // item of the drag activator, and the shared Button carries
          // `shrink-0`, so `whitespace-normal` alone still cannot wrap it.
          // The descendant selector out-specifies `.shrink-0`.
          "flex items-center h-full min-w-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:max-w-full [&>button]:overflow-hidden",
          !isDragging && "cursor-grab",
          isDragging && "cursor-grabbing"
        )}
      >
        {children}
      </div>

      {canResize && (
        <div
          onDoubleClick={resetSize}
          onMouseDown={(e) => {
            e.stopPropagation();
            (resizeHandler as React.MouseEventHandler)(e);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            (resizeHandler as React.TouchEventHandler)(e);
          }}
          className="absolute end-0 top-0 h-full w-4 cursor-col-resize select-none touch-none flex items-center justify-center bg-transparent hover:bg-blue-500/10 z-10"
          style={{ userSelect: "none" }}
        >
          <div
            className={cn(
              "w-px h-[70%] border-e border-border hover:border-blue-500",
              isResizing && "border-blue-500 border-e-2"
            )}
          />
        </div>
      )}
    </TableHead>
  )
}
